// components/steps/Step2RoleSelection.tsx - Updated with SVG Icons and Dynamic Button Colors

import React, { useState } from 'react';
import { Gate, Personnel } from '../../types/GatePassTypes';
import { GateSelectionPopup } from '../../../workFlow/GateSelectionPopup';
import { PersonnelSelectionPopup } from '../../../workFlow/PersonnelSelectionPopup';
import { PersonnelViewWrapper } from '../../../docAI/PersonnelView';
import { Gate as GateManagementGate } from '../../../gateManagement/types';
import './Step2.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface Step2RoleSelectionProps {
  formData: {
    gateId?: string;
    visitorId?: string;
    approverId?: string;
    reviewerId?: string;
  };
  selectedGates?: Gate[];
  selectedPersonnel?: Personnel[];
  personnelVisaIssueIds?: number[];
  regionId?: number | null;
  getAccessToken: () => Promise<string>;
  onStatusUpdate: (status: { type: 'success' | 'error' | null; message: string }) => void;
  onGatesChange?: (gates: Gate[]) => void;
  onPersonnelChange?: (personnel: Personnel[]) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onValidationChange?: (isValid: boolean) => void;
}



// Type transformer to convert between Gate interfaces
const transformGateToGatePassGate = (gateManagementGate: GateManagementGate): Gate => ({
  gateId: gateManagementGate.gateId,
  gateNumber: gateManagementGate.gateNumber,
  locationName: gateManagementGate.description || `Gate ${gateManagementGate.gateNumber}`,
  locationId: gateManagementGate.regionId,
  regionId: gateManagementGate.regionId,
  isActive: gateManagementGate.isActive
});

const transformGatePassGateToGateManagement = (gatePassGate: Gate): GateManagementGate => ({
  gateId: gatePassGate.gateId,
  gateNumber: gatePassGate.gateNumber,
  locationName: gatePassGate.locationName,
  regionId: gatePassGate.regionId,
  isActive: gatePassGate.isActive ?? true,
  description: gatePassGate.locationName,
  approvers: [],
  reviewers: [],
  approverRole: undefined,
  reviewerRole: undefined,
  createdAt: undefined,
  updatedAt: undefined
});

export const Step2RoleSelection: React.FC<Step2RoleSelectionProps> = ({
  formData,
  selectedGates = [],
  selectedPersonnel = [],
  personnelVisaIssueIds = [],
  regionId,
  getAccessToken,
  onStatusUpdate,
  onGatesChange,
  onPersonnelChange,
  onChange,
  onValidationChange
}) => {
  // Popup states
  const [showGateSelection, setShowGateSelection] = useState(false);
  const [showPersonnelSelection, setShowPersonnelSelection] = useState(false);

  // Personnel detail view state
  const [viewingPersonnel, setViewingPersonnel] = useState<any>(null);
  const [isLoadingPersonnelDetail, setIsLoadingPersonnelDetail] = useState(false);

  // Check if items are selected for button styling
  const hasGatesSelected = selectedGates.length > 0;
  const hasPersonnelSelected = selectedPersonnel.length > 0;

  // Compute expired documents directly from personnel data
  // This catches expired passports/visas even when personnelVisaIssueIds
  // isn't populated (e.g. after loading a draft)
  const localExpiredPersonnelIds = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selectedPersonnel
      .filter((person) => {
        const personAny = person as any;
        const passportExpiry = personAny.passportExpiryDate || personAny.passportExpiry;
        const visaExpiry = person.visaExpiry;

        const isPassportExpired = passportExpiry ? new Date(passportExpiry) < today : false;
        const isVisaExpired = visaExpiry ? new Date(visaExpiry) < today : false;

        return isPassportExpired || isVisaExpired;
      })
      .map((person) => person.personnelId);
  }, [selectedPersonnel]);

  // Merge parent-supplied visa issue IDs with locally computed ones
  const allExpiredIds = React.useMemo(() => {
    const merged = new Set([...personnelVisaIssueIds, ...localExpiredPersonnelIds]);
    return Array.from(merged);
  }, [personnelVisaIssueIds, localExpiredPersonnelIds]);

  // Validation logic - Step is valid when gates & personnel are selected AND no expired documents
  const isStepValid = hasGatesSelected && hasPersonnelSelected && allExpiredIds.length === 0;

  // Notify parent component of validation status changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isStepValid);
    }
  }, [isStepValid, onValidationChange]);

  // Handle gate selection with type transformation
  const handleGateSelectionConfirm = (gates: GateManagementGate[]) => {
    const transformedGates = gates.map(transformGateToGatePassGate);
    if (onGatesChange) {
      onGatesChange(transformedGates);
    }
    setShowGateSelection(false);
  };

  // Handle personnel selection
  const handlePersonnelSelectionConfirm = (personnel: Personnel[]) => {
    if (onPersonnelChange) {
      onPersonnelChange(personnel);
    }
    setShowPersonnelSelection(false);
  };

  // Remove gate from selection
  const handleRemoveGate = (gateId: number) => {
    const updatedGates = selectedGates.filter(gate => gate.gateId !== gateId);
    if (onGatesChange) {
      onGatesChange(updatedGates);
    }
  };

  // Remove personnel from selection
  const handleRemovePersonnel = (personnelId: number) => {
    const updatedPersonnel = selectedPersonnel.filter(person => person.personnelId !== personnelId);
    if (onPersonnelChange) {
      onPersonnelChange(updatedPersonnel);
    }
  };

  // View personnel details in modal
  // Fetch a document SAS download URL from the authenticated endpoint
  const fetchDocumentSasUrl = async (
    personnelId: number,
    fileType: string,
    accessToken: string
  ): Promise<string | undefined> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/personnel/${personnelId}/file/${fileType}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          return data.downloadUrl;
        }
      }
    } catch (err) {
      console.warn(`⚠️ Could not fetch SAS URL for ${fileType}:`, err);
    }
    return undefined;
  };

  const handleViewPersonnel = async (person: Personnel) => {
    setIsLoadingPersonnelDetail(true);
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/personnel/${person.personnelId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      let personnelData: any;

      if (response.ok) {
        const result = await response.json();
        personnelData = result.success && result.data ? result.data : result;
        console.log('✅ Personnel detail fetched from API:', personnelData);
      } else {
        console.warn('⚠️ API returned non-OK, using local data. Status:', response.status);
        const personAny = person as any;
        personnelData = {
          ...personAny,
          fullName: `${person.firstName || ''} ${person.lastName || ''}`.trim(),
          createdAt: personAny.createdAt || '',
          updatedAt: personAny.updatedAt || ''
        };
      }

      // Fetch SAS download URLs for documents in parallel
      const [passportSasUrl, visaIdSasUrl] = await Promise.all([
        personnelData.passportDocumentFileName
          ? fetchDocumentSasUrl(person.personnelId, 'passport', accessToken)
          : Promise.resolve(undefined),
        personnelData.visaIdDocumentFileName
          ? fetchDocumentSasUrl(person.personnelId, 'visaid', accessToken)
          : Promise.resolve(undefined)
      ]);

      // Also try legacy document field
      let legacySasUrl: string | undefined;
      if (!personnelData.passportDocumentFileName && !personnelData.visaIdDocumentFileName && personnelData.documentFileName) {
        legacySasUrl = await fetchDocumentSasUrl(person.personnelId, 'document', accessToken);
      }

      // Merge SAS URLs into the data so PersonnelViewWrapper can display them
      if (passportSasUrl) {
        personnelData.passportDocumentFileUrl = passportSasUrl;
      }
      if (visaIdSasUrl) {
        personnelData.visaIdDocumentFileUrl = visaIdSasUrl;
      }
      if (legacySasUrl) {
        personnelData.documentFileUrl = legacySasUrl;
      }

      console.log('📎 Document URLs resolved:', {
        passport: personnelData.passportDocumentFileUrl ? '✅' : 'none',
        visaId: personnelData.visaIdDocumentFileUrl ? '✅' : 'none',
        legacy: personnelData.documentFileUrl ? '✅' : 'none'
      });

      setViewingPersonnel(personnelData);

    } catch (error) {
      console.error('Error fetching personnel details:', error);
      const personAny = person as any;
      setViewingPersonnel({
        ...personAny,
        fullName: `${person.firstName || ''} ${person.lastName || ''}`.trim(),
        createdAt: personAny.createdAt || '',
        updatedAt: personAny.updatedAt || ''
      });
    } finally {
      setIsLoadingPersonnelDetail(false);
    }
  };

  return (
    <div className="step-content">
      {/* Selection Sections */}
      <div className="step2-selection-container">
        
        {/* Gate Selection Section */}
        <div className="selection-section">
          <div className="s2-section-header">
            <span className="s2-section-title">Gate Selection</span>
            {hasGatesSelected && (
              <span className="s2-count-badge">{selectedGates.length} selected</span>
            )}
            <button
              type="button"
              onClick={() => setShowGateSelection(true)}
              disabled={!regionId}
              className={`selection-btn ${hasGatesSelected ? 'has-items' : ''}`}
            >
              {selectedGates.length === 0 ? 'Select Gates' : 'Modify'}
            </button>
          </div>

          {/* Region Warning */}
          {!regionId && (
            <div className="form-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">
                Please select a region in the Basic Information step before choosing gates.
              </span>
            </div>
          )}

          {/* Selected Gates Display */}
          {selectedGates.length === 0 ? (
            <div className="s2-empty-inline">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.4 }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span>No gates selected — click <strong>Select Gates</strong> to add access points</span>
            </div>
          ) : (
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Gate</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedGates.map((gate) => (
                    <tr key={gate.gateId}>
                      <td className="gate-number">
                        <strong>{gate.gateNumber}</strong>
                      </td>
                      <td className="gate-location">
                        {gate.locationName || 'Unknown Location'}
                      </td>
                      <td className="gate-status">
                        <span className={`status-text ${gate.isActive ? 'active' : 'inactive'}`}>
                          {gate.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="gate-actions">
                        <button
                          type="button"
                          onClick={() => handleRemoveGate(gate.gateId)}
                          className="simple-remove-btn"
                          title="Remove gate"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Personnel Selection Section */}
        <div className="selection-section">
          <div className="s2-section-header">
            <span className="s2-section-title">Personnel Selection</span>
            {hasPersonnelSelected && (
              <span className="s2-count-badge">{selectedPersonnel.length} selected</span>
            )}
            <button
              type="button"
              onClick={() => setShowPersonnelSelection(true)}
              className={`selection-btn ${hasPersonnelSelected ? 'has-items' : ''}`}
            >
              {selectedPersonnel.length === 0 ? 'Select Personnel' : 'Modify'}
            </button>
          </div>

          {/* Visa validation warning */}
          {allExpiredIds.length > 0 && (
            <div className="visa-warning-bar">
              ⚠️ {allExpiredIds.length} personnel {allExpiredIds.length === 1 ? 'has' : 'have'} expired documents — remove highlighted rows to proceed.
            </div>
          )}

          {/* Selected Personnel Display */}
          {selectedPersonnel.length === 0 ? (
            <div className="s2-empty-inline">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.4 }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span>No personnel selected — click <strong>Select Personnel</strong> to add individuals</span>
            </div>
          ) : (
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>ID</th>
                    <th>Nationality</th>
                    <th>Passport Expiry</th>
                    <th>Visa Expiry</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPersonnel.map((person) => {
                    const hasVisaIssue = allExpiredIds.includes(person.personnelId);
                    const personAny = person as any;
                    const passportExpiry = personAny.passportExpiryDate || personAny.passportExpiry;
                    const visaExpiry = person.visaExpiry;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const isPassportExpired = passportExpiry ? new Date(passportExpiry) < today : false;
                    const isVisaExpired = visaExpiry ? new Date(visaExpiry) < today : false;

                    return (
                    <tr 
                      key={person.personnelId}
                      className={hasVisaIssue ? 'visa-issue-row' : ''}
                    >
                      <td className="personnel-name">
                        <button
  type="button"
  className="personnel-name-link"
  onClick={() => handleViewPersonnel(person)}
  title="View personnel details"
>
  {`${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unknown'}
</button>
                       
                      </td>
                      <td className="personnel-id">
                        {person.nationalIdNo ? 
                          (person.nationalIdNo.length > 4
                            ? `${person.nationalIdNo.substring(0, 2)}xx${person.nationalIdNo.slice(-2)}`
                            : person.nationalIdNo)
                          : '—'
                        }
                      </td>
                      <td className="personnel-nationality">
                        {person.nationality || 'Unknown'}
                      </td>
                      <td className="personnel-date">
                        {passportExpiry ? (
                          <span className={isPassportExpired ? 'date-expired' : 'date-valid'}>
                            {new Date(passportExpiry).toLocaleDateString()}
                            {isPassportExpired && <span className="expiry-label">Expired</span>}
                          </span>
                        ) : (
                          <span className="date-missing">—</span>
                        )}
                      </td>
                      <td className="personnel-date">
                        {visaExpiry ? (
                          <span className={isVisaExpired ? 'date-expired' : 'date-valid'}>
                            {new Date(visaExpiry).toLocaleDateString()}
                            {isVisaExpired && <span className="expiry-label">Expired</span>}
                          </span>
                        ) : (
                          <span className="date-missing">No Visa</span>
                        )}
                      </td>
                      <td className="personnel-actions">
                        <button
                          type="button"
                          onClick={() => handleRemovePersonnel(person.personnelId)}
                          className="simple-remove-btn"
                          title="Remove personnel from selection"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Popups */}
      <GateSelectionPopup
        isOpen={showGateSelection}
        selectedGates={selectedGates.map(transformGatePassGateToGateManagement)}
        selectedRegionId={regionId || null}
        getAccessToken={getAccessToken}
        onClose={() => setShowGateSelection(false)}
        onConfirm={handleGateSelectionConfirm}
        onStatusUpdate={onStatusUpdate}
      />

      <PersonnelSelectionPopup
        isOpen={showPersonnelSelection}
        selectedPersonnel={selectedPersonnel}
        getAccessToken={getAccessToken}
        onClose={() => setShowPersonnelSelection(false)}
        onConfirm={handlePersonnelSelectionConfirm}
        onStatusUpdate={onStatusUpdate}
      />

      {/* Personnel Detail View Modal */}
      {(viewingPersonnel || isLoadingPersonnelDetail) && (
        <div 
          className="personnel-detail-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isLoadingPersonnelDetail) {
              setViewingPersonnel(null);
            }
          }}
        >
          <div className="personnel-detail-modal">
            {isLoadingPersonnelDetail ? (
              <div className="personnel-detail-loading">
                <div className="personnel-detail-spinner" />
                <p>Loading personnel details...</p>
              </div>
            ) : (
              <PersonnelViewWrapper
                personnelData={viewingPersonnel}
                onClose={() => setViewingPersonnel(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};